import {
  buildProjectViews,
  deriveWatchList,
  isSeriousWatchItem,
} from '../home/projects';
import { healthColor } from '../projectEnvironments/argoApp';
import type { ArgoApplication } from '../projectEnvironments/argoApp';
import type { ProjectEntry } from '../projectEnvironments/projectList';

/**
 * The single place where ArgoCD's health vocabulary becomes a colour in the
 * sidebar. `healthColor` in argoApp.ts stays the only source of the semantics
 * — it names the cases — and this record only says which design token each
 * name paints with: the same `--bui-fg-*` tokens a BUI `<Text color>` reads,
 * so a dot here matches the status text of the environments card. Keyed by
 * that union, so a new case there fails to compile here instead of silently
 * falling through to grey.
 */
export const healthDotColors: Record<ReturnType<typeof healthColor>, string> = {
  success: 'var(--bui-fg-success)',
  info: 'var(--bui-fg-info)',
  warning: 'var(--bui-fg-warning)',
  danger: 'var(--bui-fg-danger)',
  secondary: 'var(--bui-fg-secondary)',
};

/**
 * One project's state, for the dot of its badge in the compact menu:
 *  - `danger`: an environment broken (Degraded, Missing) — the serious items
 *    of the home page's « À surveiller » (`isSeriousWatchItem`);
 *  - `warning`: one of its other items — out of sync, an Unknown health, an
 *    ephemeral environment left idle;
 *  - `info`: nothing to watch, an environment still Progressing;
 *  - `success`: every environment fine;
 *  - `none`: no environment running at all — asleep, or never deployed (the
 *    two are told apart only by the per-project read of `permanentEnvironments.ts`,
 *    which the rail does not make for every project).
 * The home page's rules exactly (`deriveWatchList`), without the pipelines,
 * which the sidebar does not read.
 */
export type ProjectTone = 'success' | 'info' | 'warning' | 'danger' | 'none';

export function projectTone(
  project: ProjectEntry,
  appsByProject: Map<string, ArgoApplication[]>,
  now: number,
): ProjectTone {
  const views = buildProjectViews([project], appsByProject);
  const environments = views[0].environments;
  if (environments.length === 0) {
    return 'none';
  }
  const watched = deriveWatchList(views, {}, now);
  if (watched.some(isSeriousWatchItem)) {
    return 'danger';
  }
  if (watched.length > 0) {
    return 'warning';
  }
  return environments.some(env => env.healthStatus === 'Progressing')
    ? 'info'
    : 'success';
}

const ALPHANUMERIC = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

/** Upper-case letters and digits, accents dropped (« Café » → « CAFE »). */
function wordsOf(name: string): string[] {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .split(/[^A-Z0-9]+/)
    .filter(word => word.length > 0);
}

/** First letters of the first two words, or the first two of a single one. */
function primaryOf(words: string[]): string {
  if (words.length >= 2) {
    return words[0][0] + words[1][0];
  }
  return words[0].slice(0, 2);
}

function sharedPrefixLength(a: string, b: string): number {
  let length = 0;
  while (length < a.length && length < b.length && a[length] === b[length]) {
    length++;
  }
  return length;
}

/**
 * Every pair of characters a name may go by, best first: its primary
 * (`primaryOf`); its first letter and each character after the part it
 * shares with another name — what tells it apart (odooperf1 among odoodemo1,
 * odoodemo7: « OP »); its first and last characters; its first and each of
 * its characters; then any pair at all, which is what makes the assignment
 * total.
 */
function candidatesOf(joined: string, primary: string, others: string[]) {
  const first = joined[0];
  const shared = Math.max(
    1,
    ...others.map(other => sharedPrefixLength(joined, other)),
  );
  const candidates = [primary];
  for (const char of joined.slice(shared)) {
    candidates.push(first + char);
  }
  candidates.push(first + joined[joined.length - 1]);
  for (const char of joined.slice(1)) {
    candidates.push(first + char);
  }
  for (const a of first + ALPHANUMERIC) {
    for (const b of ALPHANUMERIC) {
      candidates.push(a + b);
    }
  }
  return candidates;
}

/**
 * Two characters per project for its badge in the compact menu, distinct
 * within the list: « Café Volcan » CV, « fabodoo2 » FA, and among
 * odoodemo1, odoodemo7, odooperf1 — whose « OD » nobody gets — O1, O7, OP.
 *
 * Each name is its display label, or its id where the label has no letter
 * or digit. In the list's order, each project takes its first candidate
 * (`candidatesOf`) that no project before it took and that is not ANOTHER
 * project's primary: a primary held by one project alone is always its own,
 * and a primary two projects share is nobody's.
 */
export function projectInitials(
  projects: ReadonlyArray<{ label: string; projectId: string }>,
): string[] {
  const joined = projects.map(project => {
    const words = wordsOf(project.label);
    const chosen = words.length > 0 ? words : wordsOf(project.projectId);
    return { words: chosen, text: chosen.join('') };
  });
  const primaries = joined.map(({ words, text }) =>
    text.length > 0 ? primaryOf(words) : '?',
  );
  const used = new Set<string>();

  return joined.map(({ text }, index) => {
    const primary = primaries[index];
    if (text.length === 0) {
      return primary;
    }
    const reserved = (candidate: string) =>
      primaries.some((other, at) => at !== index && other === candidate);
    const others = joined
      .filter((_, at) => at !== index)
      .map(other => other.text);
    const chosen = candidatesOf(text, primary, others).find(
      candidate => !used.has(candidate) && !reserved(candidate),
    );
    // Only past 1 296 projects sharing their first letter: the tooltip and
    // the accessible name still tell them apart.
    const initials = chosen ?? primary;
    used.add(initials);
    return initials;
  });
}
